import { useCallback, useState, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';

import {
  NotificationService,
  NOTIFICATION_TYPE_ERROR,
  NOTIFICATION_TYPE_SUCCESS,
  NOTIFICATION_DURATIONS,
} from 'ui-components/services/notification-service';

import { useDemoOrdersLabsHelpers } from './use-demo-orders-labs-helpers';
import { useLabsData } from './use-labs-data';
import { LABS_FEATURE_ENABLED, LABS_FEATURE_DISABLED } from '../../../../constants/analytics-events';
import { DemoOrders, LossPrevention } from '../../../../constants/features';
import { LossPreventionService } from '../../../../services/loss-prevention-service';
import { Segment } from '../../../../services/analytics-services/segment';
import { featureMessages } from '../labs-feature-messages';
import { messages } from '../messages';

/** @typedef {import('../typedefs').LabsFeature} LabsFeature */

/**
 * @typedef {object} UseLabsActionsReturn
 * @property {(feature: LabsFeature) => Promise<boolean>} handleEnableFeature - Enables a feature with notifications, analytics, and special handling
 * @property {(feature: LabsFeature, feedback?: string) => Promise<void>} handleDisableFeature - Disables a feature with notifications, analytics, and optional feedback
 * @property {(feature: LabsFeature) => boolean} isFeatureUpdating - Returns whether a feature is currently being updated
 * @property {(featureId: string) => boolean | undefined} getOptimisticEnabledState - Returns optimistic enabled state for a feature (undefined if no optimistic state)
 */

/**
 * Hook providing actions with notifications and analytics for Labs features.
 * Wraps pure API calls from use-labs-data with side effects.
 * @returns {UseLabsActionsReturn} Action functions and state accessors
 */
export const useLabsActions = () => {
  const { enableFeature, disableFeature, enabledFeatures } = useLabsData();
  const { handleDemoOrdersEnable, handleDemoOrdersDisable } = useDemoOrdersLabsHelpers();
  const [updatingFeatures, setUpdatingFeatures] = useState(/** @type {Set<string>} */ (new Set()));
  const [optimisticEnabledFeatures, setOptimisticEnabledFeatures] = useState(/** @type {Map<string, boolean>} */ (new Map()));

  useEffect(() => {
    if (optimisticEnabledFeatures.size === 0) return;

    const toRemove = /** @type {string[]} */ ([]);
    const timerId = setTimeout(() => {
      setOptimisticEnabledFeatures(new Map());
    }, 15000);

    optimisticEnabledFeatures.forEach((optimisticState, featureId) => {
      const actualState = enabledFeatures.has(featureId);
      if (optimisticState === actualState) {
        toRemove.push(featureId);
      }
    });

    if (toRemove.length > 0) {
      setOptimisticEnabledFeatures((prev) => {
        const next = new Map(prev);
        toRemove.forEach((featureId) => next.delete(featureId));
        return next;
      });
    }

    return () => clearTimeout(timerId);
  }, [enabledFeatures, optimisticEnabledFeatures]);

  /**
   * Sets a feature's updating state
   * @param {string} labsFeatureId - Labs feature identifier
   * @param {boolean} isFeatureUpdating - Whether feature is being updated
   */
  const setFeatureUpdating = useCallback((labsFeatureId, isFeatureUpdating) => {
    setUpdatingFeatures((prev) => {
      const next = new Set(prev);
      if (isFeatureUpdating) {
        next.add(labsFeatureId);
      } else {
        next.delete(labsFeatureId);
      }
      return next;
    });
  }, []);

  /**
   * Sets optimistic enabled state for a feature
   * @param {string} featureId - Feature identifier
   * @param {boolean} isEnabled - Optimistic enabled state
   */
  const setOptimisticEnabled = useCallback((featureId, isEnabled) => {
    setOptimisticEnabledFeatures((prev) => {
      const next = new Map(prev);
      next.set(featureId, isEnabled);
      return next;
    });
  }, []);

  /**
   * Clears optimistic state for a feature (used on errors)
   * @param {string} featureId - Feature identifier
   */
  const clearOptimisticState = useCallback((featureId) => {
    setOptimisticEnabledFeatures((prev) => {
      const next = new Map(prev);
      next.delete(featureId);
      return next;
    });
  }, []);

  /**
   * Enables a Labs feature with optimistic UI updates, notifications, and analytics tracking.
   * @param {LabsFeature} feature - Feature to enable
   * @returns {Promise<boolean>} Whether the feature was successfully enabled
   */
  const handleEnableFeature = useCallback(
    async (feature) => {
      if (!feature.isLabsManaged || !feature.labsFeatureId) {
        NotificationService.showNotification({
          title: '',
          notificationType: NOTIFICATION_TYPE_ERROR,
          content: <FormattedMessage {...messages.featureUpdateError} />,
          duration: NOTIFICATION_DURATIONS.SHORT,
        });
        return false;
      }

      setOptimisticEnabled(feature.id, true);
      setFeatureUpdating(feature.labsFeatureId, true);

      try {
        await enableFeature(feature.labsFeatureId);

        const isDemoOrdersFeature = feature.labsFeatureId === DemoOrders;
        if (isDemoOrdersFeature) {
          handleDemoOrdersEnable();
        }

        Segment.trackEvent(LABS_FEATURE_ENABLED, {
          featureId: feature.id,
          labsFeatureId: feature.labsFeatureId,
          featureTitle: featureMessages[feature.titleKey]?.defaultMessage || feature.titleKey,
          featureCategory: feature.category,
        });

        NotificationService.showNotification({
          title: '',
          notificationType: NOTIFICATION_TYPE_SUCCESS,
          content: (
            <FormattedMessage
              {...messages.featureEnabledSuccess}
              values={{ featureName: featureMessages[feature.titleKey]?.defaultMessage || feature.titleKey }}
            />
          ),
          duration: NOTIFICATION_DURATIONS.SHORT,
        });

        return true;
      } catch {
        clearOptimisticState(feature.id);

        NotificationService.showNotification({
          title: '',
          notificationType: NOTIFICATION_TYPE_ERROR,
          content: <FormattedMessage {...messages.featureUpdateError} />,
          duration: NOTIFICATION_DURATIONS.SHORT,
        });

        return false;
      } finally {
        setFeatureUpdating(feature.labsFeatureId, false);
        // Don't clear optimistic state here - let it persist until actual state updates
        // This prevents slider flickering while getSeller() is in flight
      }
    },
    [enableFeature, setFeatureUpdating, setOptimisticEnabled, clearOptimisticState, handleDemoOrdersEnable],
  );

  /**
   * Disables a Labs feature with optimistic UI updates, notifications, analytics tracking, and optional feedback.
   * @param {LabsFeature} feature - Feature to disable
   * @param {string} [feedback=''] - Optional user feedback about why they're disabling the feature
   * @returns {Promise<void>}
   */
  const handleDisableFeature = useCallback(
    async (feature, feedback = '') => {
      if (!feature.isLabsManaged || !feature.labsFeatureId) {
        NotificationService.showNotification({
          title: '',
          notificationType: NOTIFICATION_TYPE_ERROR,
          content: <FormattedMessage {...messages.featureUpdateError} />,
          duration: NOTIFICATION_DURATIONS.SHORT,
        });
        return;
      }

      setOptimisticEnabled(feature.id, false);
      setFeatureUpdating(feature.labsFeatureId, true);

      try {
        const isDemoOrdersFeature = feature.labsFeatureId === DemoOrders;
        if (isDemoOrdersFeature) {
          handleDemoOrdersDisable();
        }

        // Turn off Loss Prevention system when disabling in Labs
        const isLossPreventionFeature = feature.labsFeatureId === LossPrevention;
        if (isLossPreventionFeature) {
          const currentSettings = await LossPreventionService.getSettings();
          await LossPreventionService.updateSettings({
            ...currentSettings,
            isEnabled: false,
          });
        }

        await disableFeature(feature.labsFeatureId);

        Segment.trackEvent(LABS_FEATURE_DISABLED, {
          featureId: feature.id,
          labsFeatureId: feature.labsFeatureId,
          featureTitle: featureMessages[feature.titleKey]?.defaultMessage || feature.titleKey,
          featureCategory: feature.category,
          feedback: feedback.trim() || null,
        });

        NotificationService.showNotification({
          title: '',
          notificationType: NOTIFICATION_TYPE_SUCCESS,
          content: (
            <FormattedMessage
              {...messages.featureDisabledSuccess}
              values={{ featureName: featureMessages[feature.titleKey]?.defaultMessage || feature.titleKey }}
            />
          ),
          duration: NOTIFICATION_DURATIONS.SHORT,
        });
      } catch {
        clearOptimisticState(feature.id);

        NotificationService.showNotification({
          title: '',
          notificationType: NOTIFICATION_TYPE_ERROR,
          content: <FormattedMessage {...messages.featureUpdateError} />,
          duration: NOTIFICATION_DURATIONS.SHORT,
        });
      } finally {
        setFeatureUpdating(feature.labsFeatureId, false);
        // Don't clear optimistic state here - let it persist until actual state updates
        // This prevents slider flickering while getSeller() is in flight
      }
    },
    [disableFeature, setFeatureUpdating, setOptimisticEnabled, clearOptimisticState, handleDemoOrdersDisable],
  );

  /**
   * Checks if a feature is currently being updated (enabled or disabled).
   * Returns false for non-Labs-managed features.
   * Used to show loading states in UI (e.g., disabled toggle with spinner).
   * @param {LabsFeature} feature - Feature to check
   * @returns {boolean} True if feature is currently being updated
   */
  const isFeatureUpdating = useCallback(
    (feature) => {
      if (!feature.isLabsManaged || !feature.labsFeatureId) {
        return false;
      }
      return updatingFeatures.has(feature.labsFeatureId);
    },
    [updatingFeatures],
  );

  /**
   * Gets the optimistic enabled state for a feature.
   * Returns undefined if no optimistic state exists (use actual state).
   * Used to provide immediate UI feedback before server confirms state change.
   * @param {string} featureId - Feature identifier
   * @returns {boolean | undefined} Optimistic enabled state, or undefined if no optimistic state
   */
  const getOptimisticEnabledState = useCallback(
    (featureId) => {
      return optimisticEnabledFeatures.get(featureId);
    },
    [optimisticEnabledFeatures],
  );

  return {
    handleEnableFeature,
    handleDisableFeature,
    isFeatureUpdating,
    getOptimisticEnabledState,
  };
};
